import { useEffect, useRef, useState } from "react";
import { Bell, Copy, Film, Image as ImageIcon, Play, RefreshCw, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { copyText } from "../../lib/publisherTools";

const inputCls = "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";
const cardCls = "rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-5";
const btnCls = "inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors";

export default function KickAlerts() {
  const [edits, setEdits] = useState([]);
  const [settings, setSettings] = useState(null);
  const [username, setUsername] = useState("");
  const [volume, setVolume] = useState(100);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const testMediaRef = useRef(null);

  const load = () => {
    api.get("/admin/kick/edits").then((r) => setEdits(r.data)).catch(() => {});
    api.get("/admin/kick/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    const uname = username.trim().replace(/^@+/, "").toLowerCase();
    if (!uname || !file) { toast.error("Kick kullanıcı adı ve edit dosyası gerekli"); return; }
    if (saving) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/admin/kick/edits/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/kick/edits", { kick_username: uname, file_url: up.data.url, file_kind: up.data.kind, volume });
      toast.success(`@${uname} kaydedildi`);
      setUsername(""); setFile(null); setVolume(100);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const updateVolume = async (id, v) => {
    try {
      await api.put(`/admin/kick/edits/${id}`, { volume: v });
      setEdits((prev) => prev.map((e) => (e.id === id ? { ...e, volume: v } : e)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/kick/edits/${id}`);
      setEdits((prev) => prev.filter((e) => e.id !== id));
      toast.success("Silindi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const test = async (uname, fileUrl, vol) => {
    // Önceki test sesini durdur ve sıfırla — birden fazla ses aynı anda çalmasın
    if (testMediaRef.current) {
      testMediaRef.current.pause();
      testMediaRef.current.removeAttribute("src");
      testMediaRef.current = null;
    }
    // Click event içinde doğrudan çal → tarayıcı autoplay engeline takılmaz
    const master = settings?.master_volume ?? 80;
    const a = new Audio(fileUrl);
    a.volume = Math.max(0, Math.min(1, ((vol ?? 100) / 100) * (master / 100)));
    testMediaRef.current = a;
    window.__kickTestAudios = (window.__kickTestAudios || []).concat(a);
    a.play().catch(() => {});
    try {
      await api.post("/admin/kick/test", { kick_username: uname });
      toast.success(`@${uname} testi bu sayfada çalıyor — overlay açıksa orada da gösterilir`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveMaster = async (v) => {
    try {
      const r = await api.post("/admin/kick/settings", { master_volume: v });
      setSettings(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveDuration = async (v) => {
    try {
      const r = await api.post("/admin/kick/settings", { image_duration: v });
      setSettings(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const regenerate = async () => {
    try {
      const r = await api.post("/admin/kick/settings", { regenerate_token: true });
      setSettings(r.data);
      toast.success("Yeni overlay linki üretildi — OBS'teki eski link artık çalışmaz");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const overlayUrl = settings ? `${window.location.origin}/overlay/kick/${settings.overlay_token}` : "";

  return (
    <div data-testid="kick-alerts-page" className="space-y-6">
      <h1 className="font-display font-700 text-lg text-white flex items-center gap-2"><Bell size={18} className="text-cyan-300" /> Kick Abonelik Uyarıları</h1>

      <div className={cardCls} data-testid="kick-overlay-link-card">
        <h4 className="text-[11px] font-bold text-cyan-300 mb-3">OBS OVERLAY LİNKİ</h4>
        <div className="flex flex-wrap items-center gap-2">
          <input readOnly value={overlayUrl} data-testid="kick-overlay-url" className={`${inputCls} flex-1 min-w-[260px] font-mono text-xs`} />
          <button onClick={() => copyText(overlayUrl).then(() => toast.success("Link kopyalandı — OBS tarayıcı kaynağına yapıştırın"))} data-testid="kick-copy-overlay" className={`${btnCls} text-cyan-300 border-cyan-400/30 hover:bg-cyan-400/10`}><Copy size={12} /> Kopyala</button>
          <button onClick={regenerate} data-testid="kick-regen-token" className={`${btnCls} text-amber-300 border-amber-400/30 hover:bg-amber-400/10`}><RefreshCw size={12} /> Yenile</button>
        </div>
        <p className="text-[10px] text-[#64748B] mt-2">OBS → Kaynaklar → Tarayıcı (Browser) → URL'ye bu linki yapıştırın. Boyut önerisi: 1280x720.</p>
      </div>

      <div className={`${cardCls} grid grid-cols-1 md:grid-cols-2 gap-4`} data-testid="kick-global-settings">
        <div>
          <h4 className="text-[11px] font-bold text-cyan-300 mb-2">GENEL SES SEVİYESİ <span className="text-white">{settings?.master_volume ?? 80}%</span></h4>
          <input type="range" min="0" max="100" value={settings?.master_volume ?? 80} data-testid="kick-master-volume"
            onChange={(e) => setSettings((s) => ({ ...s, master_volume: Number(e.target.value) }))}
            onMouseUp={() => saveMaster(settings.master_volume)} onTouchEnd={() => saveMaster(settings.master_volume)}
            className="w-full accent-cyan-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-bold text-cyan-300 mb-2">GÖRSEL SÜRESİ <span className="text-white">{settings?.image_duration ?? 6} sn</span></h4>
          <input type="range" min="2" max="30" value={settings?.image_duration ?? 6} data-testid="kick-image-duration"
            onChange={(e) => setSettings((s) => ({ ...s, image_duration: Number(e.target.value) }))}
            onMouseUp={() => saveDuration(settings.image_duration)} onTouchEnd={() => saveDuration(settings.image_duration)}
            className="w-full accent-cyan-400" />
          <p className="text-[10px] text-[#64748B] mt-1">Görsel editler ekranda bu kadar kalır; videolar bitene kadar oynar.</p>
        </div>
      </div>

      <div className={cardCls} data-testid="kick-add-form">
        <h4 className="text-[11px] font-bold text-cyan-300 mb-3">YENİ EDİT EKLE</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">Kick kullanıcı adı</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="ornek: sezer355" data-testid="kick-username-input" className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">Edit dosyası (mp4/webm/png/gif)</label>
            <input ref={fileRef} type="file" accept="video/mp4,video/webm,image/png,image/gif,image/webp,image/jpeg" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="kick-file-input" className="text-[11px] text-[#A0ABC0] file:mr-2 file:rounded-full file:border-0 file:bg-cyan-400/10 file:text-cyan-300 file:px-3 file:py-1.5 file:text-[11px] file:font-semibold" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">Ses: %{volume}</label>
            <input type="range" min="0" max="100" value={volume} onChange={(e) => setVolume(Number(e.target.value))} data-testid="kick-edit-volume" className="w-full accent-cyan-400" />
          </div>
          <button onClick={save} disabled={saving} data-testid="kick-save-edit" className="btn-glow inline-flex items-center justify-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full disabled:opacity-50"><Upload size={13} /> {saving ? "Yükleniyor..." : "Kaydet"}</button>
        </div>
      </div>

      <div className={cardCls} data-testid="kick-edits-list">
        <h4 className="text-[11px] font-bold text-cyan-300 mb-3">KAYITLI EDİTLER <span className="text-white">({edits.length})</span></h4>
        {edits.length === 0 && <p className="text-[11px] text-[#64748B]" data-testid="kick-empty">Henüz edit eklenmedi.</p>}
        <div className="space-y-2">
          {edits.map((e) => (
            <div key={e.id} data-testid={`kick-edit-row-${e.kick_username}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.06] bg-black/30 px-4 py-3">
              <span className="text-xs font-bold text-white">@{e.kick_username}</span>
              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${e.file_kind === "video" ? "text-purple-300 bg-purple-400/10" : "text-cyan-300 bg-cyan-400/10"}`}>
                {e.file_kind === "video" ? <Film size={10} /> : <ImageIcon size={10} />} {e.file_kind === "video" ? "Video" : "Görsel"}
              </span>
              <a href={e.file_url} target="_blank" rel="noreferrer" data-testid={`kick-edit-preview-${e.kick_username}`} className="text-[10px] text-[#64748B] hover:text-cyan-300 underline underline-offset-2">önizle</a>
              <div className="flex items-center gap-2 flex-1 min-w-[140px]">
                <input type="range" min="0" max="100" value={e.volume} data-testid={`kick-edit-vol-${e.kick_username}`}
                  onChange={(ev) => setEdits((prev) => prev.map((x) => (x.id === e.id ? { ...x, volume: Number(ev.target.value) } : x)))}
                  onMouseUp={() => updateVolume(e.id, e.volume)} onTouchEnd={() => updateVolume(e.id, e.volume)}
                  className="flex-1 accent-cyan-400" />
                <span className="text-[10px] font-mono text-cyan-300 w-8">%{e.volume}</span>
              </div>
              <button onClick={() => test(e.kick_username, e.file_url, e.volume)} data-testid={`kick-test-${e.kick_username}`} className={`${btnCls} text-emerald-300 border-emerald-400/30 hover:bg-emerald-400/10`}><Play size={11} /> Test</button>
              <button onClick={() => remove(e.id)} data-testid={`kick-delete-${e.kick_username}`} className={`${btnCls} text-red-300 border-red-400/30 hover:bg-red-500/10`}><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-[#64748B]" data-testid="kick-webhook-note">
        Webhook durumu: {settings?.kick_connected ? "Kick anahtarları tanımlı" : "Kick anahtarları bekleniyor (KICK_CLIENT_ID/SECRET)"} — abonelik olayı Kick → <span className="font-mono">hoomagency.com/api/kick/webhook</span> adresine gelir; bir hediye mesajındaki her alıcı yalnızca 1 kez gösterilir, toplu hediyeler tek uyarı üretir.
      </p>
    </div>
  );
}
